import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import Select from "react-select";
import toast from "react-hot-toast";
import { isErpApiSuccess } from "../erpClient";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { hrmsApi } from "../utils/hrmsApi";
import { hrmsErrorToast } from "../utils/hrmsErrorToast";
import { hrmsSuccessMessage } from "../utils/hrmsSuccessMessage";
import { formatInr, formatMonthLabel } from "../utils/hrmsMoney";
import { HrmsBranchSelect } from "./HrmsBranchSelect";
import Pagination from "./Pagination";
import { useHrmsEmployees } from "../hooks/useHrmsEmployees";

export function AdvancesTab({ branchId, onBranchChange, branchOptions, branchesLoading }) {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const canView = hasPermission(PERMISSIONS.HRMS_ADVANCE_VIEW);
  const canEdit = hasPermission(PERMISSIONS.HRMS_ADVANCE_EDIT);

  const [employeeFilter, setEmployeeFilter] = useState(null);
  const [deductMonth, setDeductMonth] = useState("");

  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);

  const { selectOptions, loading: empLoading } = useHrmsEmployees(branchId);

  useEffect(() => {
    setRows([]);
    setCurrentPage(1);
  }, [branchId, employeeFilter?.value, deductMonth]);

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formRowId, setFormRowId] = useState(null);
  const [formEmp, setFormEmp] = useState(null);
  const [formAdvDate, setFormAdvDate] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formDeductMonth, setFormDeductMonth] = useState("");
  const [formRemarks, setFormRemarks] = useState("");

  const selectStyles = useMemo(
    () => ({
      control: (p) => ({
        ...p,
        minHeight: 44,
        minWidth: 200,
        borderRadius: buttonTableStyle === "square" ? 0 : 8,
        borderColor: "#e0e6eb",
        boxShadow: "none",
      }),
      menu: (p) => ({ ...p, zIndex: 9999 }),
    }),
    [buttonTableStyle],
  );

  const fetchList = useCallback(async () => {
    if (!branchId || !canView) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const body = { branch_id: Number(branchId) };
      if (employeeFilter?.value) body.user_admin_id = Number(employeeFilter.value);
      const dm = String(deductMonth || "").trim();
      if (/^\d{4}-\d{2}$/.test(dm)) body.deduct_in_month = dm;
      const res = await hrmsApi("advance_list", body);
      if (isErpApiSuccess(res) && res.data) {
        const list = Array.isArray(res.data.advances)
          ? res.data.advances
          : Array.isArray(res.data.data)
            ? res.data.data
            : [];
        setRows(list);
      } else {
        setRows([]);
        if (res?.success === false && res?.message) toast.error(res.message);
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not load salary advances.");
    } finally {
      setLoading(false);
    }
  }, [branchId, employeeFilter?.value, deductMonth, canView]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const displayRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, currentPage, itemsPerPage]);

  const openAdd = () => {
    setFormRowId(null);
    setFormEmp(null);
    setFormAdvDate(new Date().toISOString().split("T")[0]);
    setFormAmount("");
    setFormDeductMonth(new Date().toISOString().slice(0, 7));
    setFormRemarks("");
    setShowForm(true);
  };

  const openEdit = (r) => {
    setFormRowId(r.id != null ? Number(r.id) : null);
    const ua = Number(r.user_admin_id);
    const opt = selectOptions.find((o) => Number(o.value) === ua);
    setFormEmp(
      opt || {
        value: ua,
        label: String(r.user_name || "").trim() || `User #${ua}`,
      },
    );
    setFormAdvDate(String(r.advance_date || "").split("T")[0]);
    setFormAmount(String(r.amount ?? ""));
    const dm = r.deduct_in_month || r.month;
    setFormDeductMonth(
      dm ? String(dm).trim().slice(0, 7) : new Date().toISOString().slice(0, 7),
    );
    setFormRemarks(String(r.remarks ?? ""));
    setShowForm(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!canEdit || !branchId) {
      toast.error("You do not have permission to edit advances.");
      return;
    }
    if (!formEmp?.value) {
      toast.error("Select employee.");
      return;
    }
    const amt = Number(String(formAmount).replace(/,/g, ""));
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Enter valid amount.");
      return;
    }
    if (!/^\d{4}-\d{2}$/.test(String(formDeductMonth).trim())) {
      toast.error("Select deduct month (YYYY-MM).");
      return;
    }
    setSaving(true);
    try {
      const body = {
        branch_id: Number(branchId),
        user_admin_id: Number(formEmp.value),
        advance_date: String(formAdvDate || "").trim(),
        amount: amt,
        deduct_in_month: String(formDeductMonth).trim(),
        remarks: String(formRemarks || "").trim(),
      };
      if (formRowId != null && Number.isFinite(Number(formRowId))) {
        body.id = Number(formRowId);
      }
      const res = await hrmsApi("advance_save", body);
      if (isErpApiSuccess(res)) {
        toast.success(
          hrmsSuccessMessage(
            res,
            formRowId != null
              ? "Salary advance updated."
              : "Salary advance added.",
          ),
        );
        setShowForm(false);
        await fetchList();
      } else {
        toast.error(res?.message || "Could not save.");
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not save advance.");
    } finally {
      setSaving(false);
    }
  };

  const colCount = canEdit ? 6 : 5;

  return (
    <div className="row g-3">
      <div className="col-12 d-flex flex-wrap align-items-end gap-3">
        <HrmsBranchSelect
          branchId={branchId}
          onChangeBranchId={onBranchChange}
          branchOptions={branchOptions}
          loading={branchesLoading}
          buttonTableStyle={buttonTableStyle}
        />
        <div style={{ minWidth: 220 }}>
          <Select
            inputId="hrms-adv-emp"
            isClearable
            isLoading={empLoading}
            options={selectOptions}
            value={employeeFilter}
            onChange={setEmployeeFilter}
            placeholder="All employees"
            styles={selectStyles}
            isDisabled={!canView}
          />
        </div>
        <div>
          <Form.Control
            type="month"
            value={deductMonth}
            onChange={(ev) => setDeductMonth(ev.target.value)}
            disabled={!canView}
            aria-label="Deduct month"
            title="Deduct month"
            style={{ minWidth: 160, minHeight: 44 }}
          />
        </div>
        {canEdit && (
          <Button
            type="button"
            className={`btn btn-success ${buttonClass}`}
            disabled={!branchId || !canView}
            onClick={openAdd}
          >
            <Icon icon="solar:add-circle-bold" className="me-1" width={18} />
            Add advance
          </Button>
        )}
      </div>

      <div className="col-12">
        <div
          className={`table-responsive border ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
            }`}
        >
          <table
            className={`table text-nowrap mb-0 align-middle ${tableClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
              }`}
          >
            <thead className="text-dark fs-4">
              <tr>
                <th>Employee</th>
                <th>Advance date</th>
                <th className="text-end">Amount</th>
                <th>Deduct month</th>
                <th className="text-wrap" style={{ minWidth: "12rem" }}>
                  Remarks
                </th>
                {canEdit ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {!canView ? (
                <tr>
                  <td colSpan={colCount} className="text-center py-5 text-muted">
                    You do not have permission to view salary advances.
                  </td>
                </tr>
              ) : rows.length > 0 ? (
                displayRows.map((r, idx) => {
                  const ua = Number(r.user_admin_id);
                  const key = r.id ?? `${ua}-${idx}`;
                  const dm = r.deduct_in_month || r.month;
                  return (
                    <tr key={key}>
                      <td>{String(r.user_name || "").trim() || "—"}</td>
                      <td>{String(r.advance_date || "").split("T")[0]}</td>
                      <td className="text-end">{formatInr(r.amount)}</td>
                      <td>{dm ? formatMonthLabel(String(dm).slice(0, 7)) : "—"}</td>
                      <td className="text-wrap">{String(r.remarks || "—")}</td>
                      {canEdit ? (
                        <td>
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            className={buttonClass}
                            onClick={() => openEdit(r)}
                          >
                            <Icon icon="solar:pen-bold" width={16} />
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              ) : !loading ? (
                <tr>
                  <td colSpan={colCount} className="text-center py-5 text-muted">
                    No advances found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {canView && rows.length > 0 ? (
        <div className="col-12">
          <Pagination
            totalItems={rows.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => {
              setItemsPerPage(n);
              setCurrentPage(1);
            }}
          />
        </div>
      ) : null}

      <Modal show={showForm} onHide={() => !saving && setShowForm(false)} centered size="lg">
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{formRowId != null ? "Edit advance" : "Add advance"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={submitForm}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Employee <span className="text-danger">*</span></Form.Label>
              <Select
                inputId="hrms-adv-form-emp"
                options={selectOptions}
                value={formEmp}
                onChange={setFormEmp}
                isDisabled={saving || formRowId != null}
                styles={selectStyles}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Advance date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                value={formAdvDate}
                onChange={(ev) => setFormAdvDate(ev.target.value)}
                disabled={saving}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={formAmount}
                onChange={(ev) => setFormAmount(ev.target.value)}
                disabled={saving}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Deduct in month <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="month"
                value={formDeductMonth}
                onChange={(ev) => setFormDeductMonth(ev.target.value)}
                disabled={saving}
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formRemarks}
                onChange={(ev) => setFormRemarks(ev.target.value)}
                disabled={saving}
                placeholder="Enter remarks"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>

            <Button type="submit" variant="success" className={buttonClass} disabled={saving || !canEdit}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
